import { useRoute } from "@react-navigation/native";
import { Divider, List, ListItem, Text } from "@ui-kitten/components";
import BottomSheet from "components/BottomSheet/BottomSheet";
import Button from "components/Button";
import BuyFlowPaymentDetailCard from "components/BuyFlowPaymentDetailCard";
import NewMemberForm from "components/NewMemberForm";
import {
  addMember,
  deleteMember,
  editMember,
} from "features/slices/addMembershipFlowSlice";
import { setEditingChild, setStep } from "features/slices/buyFlowSlice";
import { BuyFlowUser } from "features/types";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import React, { useMemo, useState } from "react";
import { View } from "react-native";
import { getPlanType } from "utils/planPackageUtils";
import { getUserName } from "utils/userUtils";

const StepTwo = ({ onSubmit }: { onSubmit: () => void }) => {
  const route = useRoute();

  const dispatch = useAppDispatch();
  const [visible, setVisible] = useState(false);

  const initValues = {
    firstName: "",
    lastName: "",
    // Email needs to default to null otherwise backend will try to use the email for the username
    // which can cause an error when adding a child member.
    email: null,
    phone: "",
    minor: false,
    imageIdentifier: "",
    userFields: {},
  };

  const [newMemberFormInitValues, setNewMemberFormInitValues] =
    useState<BuyFlowUser>(initValues);
  const [editMemberIndex, setEditMemberIndex] = useState<number | null>(null);

  const { editingChild, step, signupCharge, selectedPlanPackage } =
    useAppSelector((state) => state.buyFlow);
  const { newMembers, skipAddingPlans } = useAppSelector(
    (state) => state.addMembershipFlow
  );

  const planType = useMemo(
    () => (selectedPlanPackage ? getPlanType(selectedPlanPackage) : undefined),
    [selectedPlanPackage]
  );

  const shouldShowAddChildButton = useMemo(() => {
    return planType !== "BANK" && route.name === "AddMembership";
  }, [planType, route.name]);

  const handleNewMemberSubmit = async (member: BuyFlowUser) => {
    if (editMemberIndex !== null) {
      dispatch(editMember({ index: editMemberIndex, member }));
    } else {
      dispatch(addMember(member));
    }
    setVisible(false);
    setEditMemberIndex(null);
  };

  const isUserInfoEntered = (index: number) => {
    return newMembers[index]?.firstName !== "";
  };

  return (
    <View>
      <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
        {shouldShowAddChildButton && (
          <Button
            appearance="ghost"
            testID="add-child-button"
            onPress={() => {
              dispatch(setEditingChild(true));
              setVisible(true);
              setNewMemberFormInitValues({
                ...initValues,
                minor: true,
              });
              setEditMemberIndex(null);
            }}
          >
            + Child
          </Button>
        )}
        <Button
          appearance="ghost"
          testID="add-adult-button"
          onPress={() => {
            dispatch(setEditingChild(false));
            setVisible(true);
            setNewMemberFormInitValues(initValues);
            setEditMemberIndex(null);
          }}
        >
          + Adult
        </Button>
      </View>
      <List
        data={newMembers}
        ItemSeparatorComponent={Divider}
        renderItem={({ item, index }) => {
          return (
            <ListItem
              // accessoryLeft={() =>
              //   isUserInfoEntered(item) ? (
              //     <Entypo name="check" size={24} color="green"></Entypo>
              //   ) : (
              //     <></>
              //   )
              // }
              title={(props) =>
                isUserInfoEntered(index) ? (
                  <Text {...props}>{getUserName(item)}</Text>
                ) : (
                  <Text {...props}>{editingChild ? "Child" : "Adult"}</Text>
                )
              }
              accessoryRight={
                <>
                  <Button
                    testID="edit-member-button"
                    appearance="ghost"
                    onPress={() => {
                      setEditMemberIndex(index);
                      setVisible(true);
                      setNewMemberFormInitValues(item);
                      dispatch(setEditingChild(Boolean(item.minor)));
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    appearance="ghost"
                    status="danger"
                    onPress={() => {
                      dispatch(deleteMember(index));
                    }}
                  >
                    Delete
                  </Button>
                </>
              }
            ></ListItem>
          );
        }}
      ></List>

      {signupCharge && selectedPlanPackage ? (
        <BuyFlowPaymentDetailCard
          planPackage={selectedPlanPackage}
          signupCharge={signupCharge}
        />
      ) : null}

      <Button
        style={{ marginTop: 25 }}
        onPress={() => {
          if (skipAddingPlans) {
            onSubmit();
          } else {
            dispatch(setStep(3));
          }
        }}
        testID="add-membership-step-two-button"
        disabled={newMembers.length === 0}
      >
        {skipAddingPlans
          ? `Add Member${newMembers.length > 1 ? "s" : ""} to Account`
          : "Continue"}
      </Button>
      <Button
        appearance="ghost"
        style={{ marginTop: 15 }}
        onPress={() => dispatch(setStep(step - 1))}
      >
        BACK
      </Button>

      <BottomSheet
        visible={visible}
        onClose={() => setVisible(false)}
        title={`${
          newMemberFormInitValues.firstName !== "" ? "Edit" : "Add"
        } Member`}
      >
        <View style={{ padding: 15 }}>
          <Text category="h6">{selectedPlanPackage?.name}</Text>
          <NewMemberForm
            onSubmit={handleNewMemberSubmit}
            initialValues={newMemberFormInitValues}
            editingChild={editingChild}
          />
        </View>
      </BottomSheet>
    </View>
  );
};

export default StepTwo;
